
import { useState } from "react"
import { useToast } from "@/hooks/use-toast"
import { useQuery } from "@tanstack/react-query"
import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Textarea } from "@/components/ui/textarea"
import { Label } from "@/components/ui/label"
import { supabase } from "@/integrations/supabase/client"
import type { Database } from "@/integrations/supabase/types"

type TierPrompt = Database["public"]["Tables"]["tier_prompts"]["Row"]
type SubscriptionTier = Database["public"]["Enums"]["subscription_tier"]

export default function TierPromptsSection() {
  const { toast } = useToast()
  const [selectedTier, setSelectedTier] = useState<SubscriptionTier>("essentials")

  const { data: tierPrompts } = useQuery({
    queryKey: ["tier_prompts"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("tier_prompts")
        .select("*")
        .eq("active", true)

      if (error) {
        toast({
          variant: "destructive",
          title: "Error loading tier prompts",
          description: error.message,
        })
        return []
      }

      return data as TierPrompt[]
    },
  })

  const handleSaveTierPrompt = async () => {
    const formData = new FormData(document.getElementById("tier-prompt-form") as HTMLFormElement)
    const aiPrompt = formData.get("ai_prompt") as string
    const dashboardSummary = formData.get("dashboard_summary") as string

    const { error } = await supabase.from("tier_prompts").upsert({
      subscription_tier: selectedTier,
      ai_prompt: aiPrompt,
      dashboard_summary: dashboardSummary,
    })

    if (error) {
      toast({
        variant: "destructive",
        title: "Error saving tier prompt",
        description: error.message,
      })
    } else {
      toast({
        title: "Tier prompt saved successfully",
      })
    }
  }

  const handleTierChange = (value: string) => {
    if (value === "essentials" || value === "pro" || value === "elite") {
      setSelectedTier(value)
    }
  }

  const tiers: SubscriptionTier[] = ["essentials", "pro", "elite"]
  const currentTierPrompt = tierPrompts?.find(p => p.subscription_tier === selectedTier)

  return (
    <Card>
      <CardHeader>
        <div className="flex justify-between items-center">
          <div>
            <CardTitle>Tier-Specific Prompts</CardTitle>
            <CardDescription>
              Manage AI prompts and dashboard summaries for each subscription tier
            </CardDescription>
          </div>
          <Select value={selectedTier} onValueChange={handleTierChange}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Select tier" />
            </SelectTrigger>
            <SelectContent>
              {tiers.map((tier) => (
                <SelectItem key={tier} value={tier}>
                  {tier.charAt(0).toUpperCase() + tier.slice(1)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </CardHeader>
      <CardContent>
        <form id="tier-prompt-form" className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="ai-prompt">AI Prompt Template</Label>
            <Textarea
              id="ai-prompt"
              name="ai_prompt"
              rows={10}
              defaultValue={currentTierPrompt?.ai_prompt}
              placeholder="Enter the AI prompt template for this tier..."
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="dashboard-summary">Dashboard Summary</Label>
            <Textarea
              id="dashboard-summary"
              name="dashboard_summary"
              rows={5}
              defaultValue={currentTierPrompt?.dashboard_summary}
              placeholder="Enter the summary that will be displayed on the dashboard..."
            />
          </div>
          <Button
            type="button"
            onClick={handleSaveTierPrompt}
            className="w-full"
          >
            Save {selectedTier.charAt(0).toUpperCase() + selectedTier.slice(1)} Tier Prompt
          </Button>
        </form>
      </CardContent>
    </Card>
  )
}
